'use client';
import { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { GoogleLogin } from '@react-oauth/google';
import type { WizardCtx } from '../types';
import { StepLayout, PrimaryBtn } from '../shared';
import { api, ApiError, useAuth, toast } from '@splace/core';
const HAS_GOOGLE = !!process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;

export function SignupStep({ ctx }: { ctx: WizardCtx }) {
  const [email, setEmail] = useState(ctx.data.email);
  const [loading, setLoading] = useState(false);
  const setUser = useAuth(s => s.setUser);
  const router = useRouter();
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  async function go() {
    if (!valid || loading) return;
    setLoading(true);
    try {
      await api.auth.sendOtp(email);
      ctx.update({ email });
      toast.info('Code sent — check your inbox');
      ctx.next();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to send code');
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleSuccess(idToken?: string) {
    if (!idToken) {
      toast.error('Google sign-in failed');
      return;
    }
    try {
      const result = await api.auth.google(idToken);
      setUser(result.user);
      if (result.isNewUser) {
        toast.success('Welcome — let’s finish your profile');
        router.push('/onboarding?from=google');
      } else {
        toast.success('Welcome back!');
        router.push('/feed');
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Google sign-in failed');
    }
  }

  return (
    <StepLayout
      narrow
      eyebrow="STEP 01 · CREATE ACCOUNT"
      title="Hi! Let's get you in."
      sub="Use email or one of the options below — we'll send a code to verify it's really you."
    >
      <div className="ob-form">
        <label className="ob-label">Email address</label>
        <input
          className="sp-input ob-input" type="email" placeholder="you@example.com"
          value={email} onChange={e => setEmail(e.target.value)} autoFocus
          onKeyDown={e => e.key === 'Enter' && go()}
          disabled={loading}
        />
        <PrimaryBtn full onClick={go} disabled={!valid || loading}>
          {loading ? 'Sending…' : 'Continue with email'}
        </PrimaryBtn>

        {HAS_GOOGLE && (
          <>
            <div className="ob-divider"><span>or</span></div>
            <div className="ob-google-wrap">
              <GoogleLogin
                onSuccess={(cred) => handleGoogleSuccess(cred.credential)}
                onError={() => toast.error('Google sign-in failed')}
                theme="filled_black"
                text="continue_with"
                shape="pill"
              />
            </div>
          </>
        )}

        <p className="ob-fineprint">
          By continuing you agree to Splace&apos;s <Link href="/terms">Terms</Link> and <Link href="/privacy">Privacy Policy</Link>.
        </p>
      </div>
    </StepLayout>
  );
}
